import { memo, useState, type JSX } from 'react';
import { useParams } from '@tanstack/react-router';
import { useDispatch } from 'react-redux';
import type { ImageBlock } from '@worldathletics/types';

import {
  DocumentEditorContainer,
  DocumentEditorImage,
  DocumentEditorList,
  DocumentEditorTable,
  DocumentEditorText,
} from '@/components/document';
import {
  removeNode,
  updateNode,
  type DocumentEntity,
} from '@/context/store/slices/documentDataSlice';
import { setActiveBlock } from '@/context/store/slices/siteSlice';

function DocumentEditorBlockComponent({
  node,
  childNodes,
  depth = 0,
  parentStyle = null,
}: {
  node: DocumentEntity;
  childNodes?: JSX.Element[];
  depth?: number;
  parentStyle?: string | null;
}) {
  switch (node.type) {
    case 'container':
      return (
        <DocumentEditorContainer
          node={node}
          childNodes={childNodes}
          depth={depth}
          parentStyle={parentStyle}
        />
      );

    case 'text':
      return <DocumentEditorText node={node} depth={depth} />;

    case 'table':
      return <DocumentEditorTable node={node} />;

    case 'image':
      return <ImageBlockAdapter node={node} />;

    case 'list':
      return <DocumentEditorList node={node} />;

    default:
      return (
        <div>
          Unsupported block type: {(node as { type: string }).type}
        </div>
      );
  }
}

// Adapter that bridges the Redux-dispatch convention used by the rest of the
// editor tree to the standalone DocumentEditorImage component (which exposes
// onChange/onDelete/onSelect callbacks per the images implementation plan).
// `bookId` is read from the route params to keep the parent caller signature
// (which only passes `node`) unchanged.
function ImageBlockAdapter({
  node,
}: {
  node: DocumentEntity & { type: 'image' };
}) {
  const dispatch = useDispatch();
  const { docId } = useParams({ from: '/_auth/document/$docId/$nodeId' });
  const [selected, setSelected] = useState(false);

  function handleChange(patch: Partial<ImageBlock>): void {
    dispatch(updateNode({ id: node.id, changes: patch }));
  }

  function handleDelete(): void {
    dispatch(removeNode({ id: node.id }));
  }

  return (
    <DocumentEditorImage
      block={node as ImageBlock}
      bookId={docId}
      onChange={handleChange}
      onDelete={handleDelete}
      selected={selected}
      onSelect={() => {
        setSelected(true);
        dispatch(setActiveBlock(node));
      }}
    />
  );
}

const DocumentEditorBlock = memo(DocumentEditorBlockComponent);

export { DocumentEditorBlock };
